import classNames from 'classnames';
import React from 'react';
import { FiPlus } from 'react-icons/fi';

import LastSkillParent from './LastSkillParent';
import SkillPill from './SkillPill';

import type { MappedSkillRating, OverlaySkill, SkillTreeNodeBase } from '../types';

type Props<TSkill extends SkillTreeNodeBase<TSkill>> = {
  skill: TSkill;
  parentData: TSkill[];
  openAddSkillModal: (parentData: TSkill[]) => void;
  openEditSkillModal: (skill: TSkill, parentData: TSkill[]) => void;
  showAddSkills?: boolean;
  globalRatings?: GlobalRatingItem[];
  selectedSkills?: TSkill[] | null;
  onSelectedSkillsChange?: (skills: TSkill[]) => void;
  setIsOverlayOpenFor?: (skill: OverlaySkill<TSkill> | null) => void;
  isOpenFor?: OverlaySkill<TSkill> | number | string | null;
  isSelf?: boolean;
  isTeamManager?: boolean;
  ratingOpenFor: TSkill | number | string | null;
  setRatingOpenFor: React.Dispatch<React.SetStateAction<TSkill | number | string | null>>;
};

const SkillList = <TSkill extends SkillTreeNodeBase<TSkill>>({
  skill,
  parentData,
  openAddSkillModal,
  openEditSkillModal,
  showAddSkills,
  globalRatings,
  selectedSkills,
  onSelectedSkillsChange,
  setIsOverlayOpenFor,
  isOpenFor,
  isSelf,
  isTeamManager,
  ratingOpenFor,
  setRatingOpenFor,
}: Props<TSkill>) => {
  const { name, subSkills } = skill;

  const mappedSkillRating = selectedSkills?.reduce<MappedSkillRating>((accumulator, currentSkill) => {
    if (currentSkill.id != null) {
      accumulator[String(currentSkill.id)] = {
        id: currentSkill.id,
        rating: currentSkill.rating,
        skillStatus: currentSkill.skillStatus,
      };
    }

    return accumulator;
  }, {});

  const subSkillParentData = [...parentData, skill];

  let nestedChildrenLevel = 0;

  if (subSkills.length) {
    nestedChildrenLevel += 1;
  }

  if (subSkills.some((item) => item.subSkills.length)) {
    nestedChildrenLevel += 1;
  }

  const handlePillClick = (selectedSkill: TSkill, selectedParentData: TSkill[]) => {
    if (setIsOverlayOpenFor) {
      setIsOverlayOpenFor({ ...selectedSkill, parentData: selectedParentData } as OverlaySkill<TSkill>);
    }

    if (showAddSkills) {
      openEditSkillModal(selectedSkill, selectedParentData);
    }

    if (!showAddSkills) {
      setRatingOpenFor((previousState) => {
        const previousStateId =
          typeof previousState === 'object' && previousState !== null ? previousState.id : previousState;

        if (previousStateId === selectedSkill.id) {
          return null;
        }

        return selectedSkill;
      });
    }
  };

  return (
    <div className="skills-list__container">
      {nestedChildrenLevel === 2 && (
        <div className="first-skill-container">
          <div
            className={classNames('first-skill-container__header', {
              'first-skill-container__header--clickable': showAddSkills,
            })}
            onClick={() => showAddSkills && openEditSkillModal(skill, parentData)}
          >
            {name ?? ''}
          </div>

          {subSkills.map((secondSkill) => (
            <LastSkillParent
              key={String(secondSkill.id)}
              skill={secondSkill}
              parentData={subSkillParentData}
              openAddSkillModal={openAddSkillModal}
              openEditSkillModal={openEditSkillModal}
              showAddSkills={showAddSkills}
              globalRatings={globalRatings}
              mappedSkillRating={mappedSkillRating}
              selectedSkills={selectedSkills}
              onSelectedSkillsChange={onSelectedSkillsChange}
              setIsOverlayOpenFor={setIsOverlayOpenFor}
              isOpenFor={isOpenFor}
              isSelf={isSelf}
              isTeamManager={isTeamManager}
              ratingOpenFor={ratingOpenFor}
              setRatingOpenFor={setRatingOpenFor}
            />
          ))}
        </div>
      )}

      {nestedChildrenLevel === 1 && (
        <LastSkillParent
          skill={skill}
          parentData={parentData}
          openAddSkillModal={openAddSkillModal}
          openEditSkillModal={openEditSkillModal}
          showAddSkills={showAddSkills}
          globalRatings={globalRatings}
          mappedSkillRating={mappedSkillRating}
          selectedSkills={selectedSkills}
          onSelectedSkillsChange={onSelectedSkillsChange}
          setIsOverlayOpenFor={setIsOverlayOpenFor}
          isOpenFor={isOpenFor}
          isSelf={isSelf}
          isTeamManager={isTeamManager}
          ratingOpenFor={ratingOpenFor}
          setRatingOpenFor={setRatingOpenFor}
        />
      )}

      {nestedChildrenLevel === 0 && (
        <SkillPill
          skill={skill}
          onClick={() => handlePillClick(skill, parentData)}
          globalRatings={globalRatings}
          mappedSkillRating={mappedSkillRating}
          selectedSkills={selectedSkills}
          onSelectedSkillsChange={onSelectedSkillsChange}
          setIsOverlayOpenFor={setIsOverlayOpenFor}
          isOpenFor={isOpenFor}
          isSelf={isSelf}
          isTeamManager={isTeamManager}
          ratingOpenFor={ratingOpenFor}
          setRatingOpenFor={setRatingOpenFor}
        />
      )}

      {showAddSkills && nestedChildrenLevel === 2 && (
        <div className="first-skill-container__link">
          <FiPlus color="#29B6F6" size={14} />
          <button className="text" onClick={() => openAddSkillModal(subSkillParentData)}>
            Add skill under {skill.name ?? ''}
          </button>
        </div>
      )}
    </div>
  );
};

export default SkillList;
